import { isNullish } from '@apollo/client/cache/inmemory/helpers';
import {
  ApolloCache,
  ApolloClient,
  gql,
  QueryHookOptions,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  DataActionRule,
  ManageConversationLabelsActionRule,
  SignalActionRule,
} from 'src/graphql/entities/behavior/behavior.types';
import {
  ConversationAssigneeGroupOption,
  useConversationAssigneeGroupsOptions,
} from 'src/graphql/entities/conversation-assignee-group';
import {
  excludePlatformWhenDenied,
  usePlatformCatalogAccess,
} from 'src/graphql/entities/group-rules/platform-catalog-access';
import {
  GET_PARTNER_SETTINGS_QUERY,
  GetPartnerSettingValueQuery,
  PartnerSetting,
  usePartnerSettings,
} from 'src/graphql/entities/partner-setting';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { ConditionInput, QueryVariables } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { ConversationAssigneeGroupRaw, MacroRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { mapFormValuesToMacrosRequest, mapMacroResponseToUI } from './macro.mappings';
import { MacrosFormModel } from './macro.types';

const MACRO_FIELDS = gql`
  fragment MacroFields on Macro {
    id
    macroId
    partnerId
    name
    description
    template
    aiPromptId
    aiPromptText
    actions
    messagePrivacy
    public
    createdBy
    createdAt
    updatedAt
  }
`;

const MACRO_RELATIONS = gql`
  fragment MacroRelations on Macro {
    id
    conversationAssigneeGroups {
      id
      conversationAssigneeGroupId
      name
    }
  }
`;

// This query returns only those macros which are available for the user to use in chat messages.
const GET_ENABLED_MACROS_QUERY = gql`
  query GetEnabledMacros(
    $partnerId: Int!
    $requesterId: String!
    $filters: [ConditionInput!]
  ) {
    getEnabledMacros(partnerId: $partnerId, requesterId: $requesterId, filters: $filters) {
      ...MacroFields
      ...MacroRelations
    }
  }
  ${MACRO_FIELDS}
  ${MACRO_RELATIONS}
`;

interface GetEnabledMacrosQuery {
  getEnabledMacros: FetchedMacroRaw[];
}

// This query returns only those macros which are available for the user to view/change in settings.
const GET_SETTINGS_MACROS_QUERY = gql`
  query GetManagementMacros(
    $partnerId: Int!
    $requesterId: String!
    $filters: [ConditionInput!]
  ) {
    getManagementMacros(partnerId: $partnerId, requesterId: $requesterId, filters: $filters) {
      ...MacroFields
      ...MacroRelations
    }
  }
  ${MACRO_FIELDS}
  ${MACRO_RELATIONS}
`;

interface GetSettingsMacrosQuery {
  getManagementMacros: FetchedMacroRaw[];
}

const CREATE_MACRO_MUTATION = gql`
  mutation CreateMacro($params: MacroDataInput!) {
    createMacro(data: $params) {
      ...MacroFields
      ...MacroRelations
    }
  }
  ${MACRO_FIELDS}
  ${MACRO_RELATIONS}
`;
const CREATE_MACRO_TO_ASSIGNEE_GROUP_MUTATION = gql`
  mutation CreateMacroToAssigneeGroup($params: MacroToConversationAssigneeGroupDataInput!) {
    createMacroToConversationAssigneeGroup(data: $params) {
      macroId
      conversationAssigneeGroupId
    }
  }
`;

const DELETE_MACRO_MUTATION = gql`
  mutation DeleteMacroWithRelatedData($params: DeleteMacroWithRelatedDataInput!) {
    deleteMacroWithRelatedData(params: $params) {
      macroId
    }
  }
`;

const UPDATE_MACRO_MUTATION = gql`
  mutation UpdateMacro($macroId: String!, $params: MacroDataInput!) {
    updateMacro(
      conditions: [{ attribute: "macroId", operator: "=", value: $macroId }]
      data: $params
    ) {
      ...MacroFields
    }
  }
  ${MACRO_FIELDS}
`;

const DELETE_MACRO_TO_ASSIGNEE_GROUP_MUTATION = gql`
  mutation DeleteMacroToAssigneeGroup($conditions: [ConditionInput!]!) {
    deleteMacroToConversationAssigneeGroup(conditions: $conditions) {
      macroId
      conversationAssigneeGroupId
    }
  }
`;

const INTERPOLATE_MACROS_TEMPLATE_QUERY = gql`
  query InterpolateMacrosTemplate($input: GetTextInterpolationInput!) {
    getTextInterpolation(input: $input)
  }
`;

const DISABLE_MACRO_MUTATION = gql`
  mutation DisableMacro($macroId: String!, $partnerId: Int!) {
    disableMacro(params: { macroId: $macroId, partnerId: $partnerId }) {
      macroId
      success
    }
  }
`;

const ENABLE_MACRO_MUTATION = gql`
  mutation EnableMacro($macroId: String!, $partnerId: Int!) {
    enableMacro(params: { macroId: $macroId, partnerId: $partnerId }) {
      macroId
      success
    }
  }
`;

type GetInterpolateMacrosTemplateQuery = {
  getTextInterpolation: string;
};

export interface FetchedMacroRaw extends Omit<MacroRaw, 'conversationAssigneeGroups'> {
  conversationAssigneeGroups: {
    id: string;
    conversationAssigneeGroupId: string;
    name: string;
  }[];
}

export type MacroAction = (
  | DataActionRule
  | ManageConversationLabelsActionRule
  | SignalActionRule
) & {
  id?: string;
  isValid?: boolean;
};

export interface Macro
  extends Pick<
    FetchedMacroRaw,
    | 'id'
    | 'name'
    | 'description'
    | 'template'
    | 'public'
    | 'messagePrivacy'
    | 'partnerId'
    | 'createdBy'
    | 'createdAt'
    | 'updatedAt'
  > {
  actions: MacroAction[];
  isDefault: boolean;
  conversationAssigneeGroups: ConversationAssigneeGroupOption[];
  /** Reference to a saved `AiPrompt`. Mutually exclusive with `aiPromptText`. */
  aiPromptId: string | null;
  /** Prompt written on the macro itself. Mutually exclusive with `aiPromptId`. */
  aiPromptText: string | null;
}

type UseSettingsMacrosOptions = QueryHookOptions<GetSettingsMacrosQuery> & {
  desc?: boolean;
  filters?: ConditionInput[];
  partnerId?: number | null;
};

export const useSettingsMacros = (options?: UseSettingsMacrosOptions) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const partnerId = isNullish(options?.partnerId) ? selectedPartnerId : options?.partnerId;
  const { user } = useCurrentUserProfile();
  const requesterId = user?.id;

  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const conversationAssigneeGroupOptions = useConversationAssigneeGroupsOptions(partnerId);
  const { data: disabledMacrosIds } = usePartnerSettings({
    partnerSetting: PartnerSetting.DisabledMacros,
    partnerId,
    splitPartnerAndPlatformData: true,
  });

  const { includePlatformLevelData: includePlatformMacros } = usePlatformCatalogAccess(
    'Macro',
    partnerId,
  );

  const normalizedFilters = useMemo(() => {
    // ensure empty filters don't create a distinct
    // Apollo cache entry ([] vs undefined)
    if (!options?.filters?.length) {
      return undefined;
    }

    return options.filters;
  }, [options?.filters]);

  const queryVariables = useMemo(() => {
    const baseVariables = {
      partnerId: partnerId ?? PLATFORM_PARTNER_ID,
      requesterId,
    } as {
      partnerId: number;
      requesterId: string | undefined;
      filters?: ConditionInput[];
    };

    if (normalizedFilters) {
      return {
        ...baseVariables,
        filters: normalizedFilters,
      };
    }

    return baseVariables;
  }, [normalizedFilters, partnerId, requesterId]);

  const queryOptions = useMemo<QueryHookOptions<GetSettingsMacrosQuery>>(
    () => ({
      skip: !requesterId || options?.skip,
      variables: queryVariables,
      context: {
        includePlatformLevelData: includePlatformMacros,
      },
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong when fetching macros, please try again');
      },
    }),
    [includePlatformMacros, options?.skip, queryVariables, requesterId, showErrorAlert],
  );

  const {
    data: settingsMacrosData,
    client,
    variables,
    ...rest
  } = useQuery<GetSettingsMacrosQuery>(GET_SETTINGS_MACROS_QUERY, queryOptions);

  const settingsMacros = useMemo<Macro[]>(() => {
    const list = Array.isArray(settingsMacrosData?.getManagementMacros)
      ? settingsMacrosData?.getManagementMacros
      : [];

    return list
      .map((item) => {
        const groups = item.conversationAssigneeGroups || [];

        const macroAssigneeGroups =
          groups.filter(Boolean).length === 0
            ? []
            : groups.map(({ id }) => {
                const option = conversationAssigneeGroupOptions.find(
                  (opt) => opt.value === id,
                );
                return (
                  option ||
                  ({
                    id,
                    value: id,
                    label: id,
                    partnerId: item.partnerId,
                  } as ConversationAssigneeGroupOption)
                );
              });

        const macro = mapMacroResponseToUI(item);
        return {
          ...macro,
          conversationAssigneeGroups: macroAssigneeGroups,
        };
      })
      .filter(
        // Partners should not be able to see platform-level disabled macros
        (macro) =>
          macro.partnerId === partnerId || !disabledMacrosIds.platformLevel.includes(macro.id),
      )
      .filter(excludePlatformWhenDenied(includePlatformMacros))
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
  }, [
    settingsMacrosData?.getManagementMacros,
    disabledMacrosIds,
    partnerId,
    conversationAssigneeGroupOptions,
    includePlatformMacros,
  ]);

  const { enabledMacros, disabledMacros } = useMemo(() => {
    return {
      enabledMacros: settingsMacros.filter(
        (macro) => !disabledMacrosIds.partnerLevel.includes(macro.id),
      ),
      disabledMacros: settingsMacros.filter((macro) =>
        disabledMacrosIds.partnerLevel.includes(macro.id),
      ),
    };
  }, [disabledMacrosIds, settingsMacros]);

  const [createMacroMutation, { loading: createMacroLoading }] = useMutation(
    CREATE_MACRO_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong creating macro, please try again');
      },
    },
  );

  const [createMacroToAssigneeGroupMutation, { loading: createMacroToAssigneeGroupLoading }] =
    useMutation(CREATE_MACRO_TO_ASSIGNEE_GROUP_MUTATION, {
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          'Something went wrong during macro groups assignment, please try again',
        );
      },
    });

  const [deleteMacroToAssigneeGroupMutation, { loading: deleteMacroToAssigneeGroupLoading }] =
    useMutation(DELETE_MACRO_TO_ASSIGNEE_GROUP_MUTATION, {
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          'Something went wrong during macro groups unassignment, please try again',
        );
      },
    });

  const [deleteMacroMutation] = useMutation(DELETE_MACRO_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting macro, please try again');
    },
  });

  const [updateMacroMutation, { loading: updateMacroLoading }] = useMutation(
    UPDATE_MACRO_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating macro, please try again');
      },
    },
  );

  const [disableMacroMutation, { loading: disableMacroLoading }] = useMutation(
    DISABLE_MACRO_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong disabling macro, please try again');
      },
    },
  );

  const [enableMacroMutation, { loading: enableMacroLoading }] = useMutation(
    ENABLE_MACRO_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong enabling macro, please try again');
      },
    },
  );

  const refreshPlatformMacroCaches = useCallback(() => {
    client.cache.evict({
      fieldName: 'getManagementMacros',
    });
    client.cache.evict({
      fieldName: 'getEnabledMacros',
    });
    client.cache.gc();
  }, [client]);

  const createMacro = useCallback(
    async (data: MacrosFormModel): Promise<string | undefined> => {
      const resolvedPartnerId = isNullish(partnerId) ? PLATFORM_PARTNER_ID : partnerId;
      const params = {
        ...mapFormValuesToMacrosRequest(data, resolvedPartnerId),
        createdBy: requesterId,
      };

      const createdAt = new Date().toISOString();
      const tempId = `temp-id:${createdAt}`;
      const assignedGroups = (data?.conversationAssigneeGroups?.map((item) => {
        const option = conversationAssigneeGroupOptions.find(
          (opt) => opt.value === item.value,
        );

        return {
          id: item.value,
          conversationAssigneeGroupId: item.value,
          partnerId: option?.partnerId,
          name: option?.label || item.label || '',
        };
      }) || []) as ConversationAssigneeGroupRaw[];
      const assignedGroupsResponse = assignedGroups.map((item) => ({
        conversationAssigneeGroupId: item.conversationAssigneeGroupId,
        id: item.id,
        name: item.name,
      }));

      const result = await new Promise<{ createMacro: FetchedMacroRaw[] }>(
        (resolve, reject) => {
          void createMacroMutation({
            variables: { params },

            onCompleted(data: { createMacro: FetchedMacroRaw[] }) {
              resolve(data);
            },
            onError: (error) => {
              showErrorAlert('Something went wrong creating macro, please try again');
              reject(error);
            },
            optimisticResponse: {
              createMacro: [
                {
                  __typename: 'Macro',
                  ...params,
                  id: tempId,
                  macroId: tempId,
                  createdAt,
                  updatedAt: createdAt,
                  conversationAssigneeGroups: assignedGroupsResponse,
                },
              ],
            },
            update: (
              _: ApolloCache<unknown>,
              response: {
                data?: {
                  createMacro: FetchedMacroRaw[];
                };
              },
            ) => {
              if (!response.data) return;

              const createdMacro = {
                ...response.data.createMacro[0],
                conversationAssigneeGroups: assignedGroupsResponse,
              };

              addNewMacroToCache(client, {
                variables,
              })(createdMacro);
            },
          });
        },
      );

      if (!result) {
        return undefined;
      }

      const [macro] = result.createMacro;

      await Promise.all(
        assignedGroups.map(({ id, partnerId }) => {
          return createMacroToAssigneeGroupMutation({
            variables: {
              params: {
                partnerId,
                macroId: macro.macroId,
                conversationAssigneeGroupId: id,
              },
            },
          });
        }),
      );

      await client.refetchQueries({
        include: [GET_SETTINGS_MACROS_QUERY],
      });

      // Evict all GET_ENABLED_MACROS_QUERY cache entries to ensure new macro appears
      // across all partners when switching conversations
      client.cache.evict({
        fieldName: 'getEnabledMacros',
      });
      client.cache.gc();

      if (resolvedPartnerId === PLATFORM_PARTNER_ID) {
        refreshPlatformMacroCaches();
      }

      showSuccessAlert('Macro has been successfully created');

      return macro.macroId;
    },
    [
      partnerId,
      requesterId,
      createMacroMutation,
      conversationAssigneeGroupOptions,
      createMacroToAssigneeGroupMutation,
      showSuccessAlert,
      showErrorAlert,
      variables,
      client,
      refreshPlatformMacroCaches,
    ],
  );

  const updateMacro = useCallback(
    async (macroId: string, formData: MacrosFormModel, partnerId: number) => {
      const params = mapFormValuesToMacrosRequest(formData, partnerId);
      const omitIsolationModeHeader = partnerId === PLATFORM_PARTNER_ID;
      const mutationContext = omitIsolationModeHeader
        ? { omitIsolationModeHeader: true }
        : undefined;

      // Get current macro to compare assignee groups
      const currentMacro = settingsMacros.find((macro) => macro.id === macroId);
      const currentAssigneeGroups =
        currentMacro?.conversationAssigneeGroups?.map((group) => String(group.value)) || [];

      // Get new assignee groups
      const newAssigneeGroupIds =
        formData?.conversationAssigneeGroups?.map((item) => item.id) || [];

      // Find groups to add and remove
      const groupsToAdd = newAssigneeGroupIds.filter(
        (id) => !currentAssigneeGroups.includes(id),
      );
      const groupsToRemove = currentAssigneeGroups.filter(
        (groupId) => !newAssigneeGroupIds.includes(groupId),
      );

      const assignedGroupsResponse = formData?.conversationAssigneeGroups?.map((item) => ({
        conversationAssigneeGroupId: item.id,
        id: item.id,
        name: item.label || item.id,
      }));

      try {
        // Delete removed assignee group associations first
        if (groupsToRemove.length > 0) {
          await deleteMacroToAssigneeGroupMutation({
            variables: {
              conditions: [
                {
                  attribute: 'macroId',
                  operator: '=',
                  value: macroId,
                },
                {
                  attribute: 'conversationAssigneeGroupId',
                  operator: 'in',
                  value: groupsToRemove.join(','),
                },
                {
                  attribute: 'partnerId',
                  operator: '=',
                  value: String(partnerId),
                },
              ],
            },
            context: mutationContext,
            onError: (error) => {
              console.error('Error removing macro assignee groups:', error);
              showErrorAlert(`Failed to remove assignee groups from macro. Please try again.`);
            },
          });
        }
        // Create new assignee group associations
        await Promise.all(
          groupsToAdd.map((id) =>
            createMacroToAssigneeGroupMutation({
              variables: {
                params: {
                  partnerId,
                  macroId,
                  conversationAssigneeGroupId: id,
                },
              },
              context: mutationContext,
              onError: (error) => {
                console.error('Error creating macro assignee group:', error);
                showErrorAlert(`Failed to assign group "${id}" to macro. Please try again.`);
              },
            }),
          ),
        );

        const updatedAt = new Date().toISOString();

        // Update macro after group associations are handled
        await updateMacroMutation({
          variables: {
            macroId,
            params,
          },
          context: mutationContext,
          optimisticResponse: {
            updateMacro: [
              {
                __typename: 'Macro',
                ...params,
                id: macroId,
                macroId,
                createdBy: currentMacro?.createdBy ?? '',
                createdAt: currentMacro?.createdAt || updatedAt,
                updatedAt,
                conversationAssigneeGroups: assignedGroupsResponse,
              },
            ],
          },
          update(_, { data }: { data?: { updateMacro: FetchedMacroRaw[] } }) {
            if (!data?.updateMacro?.[0]) return;

            const updatedMacro = {
              ...data.updateMacro[0],
              conversationAssigneeGroups: assignedGroupsResponse,
            };

            addNewMacroToCache(client, {
              variables: {
                partnerId,
                requesterId,
              },
            })(updatedMacro);
          },
          onError: (error) => {
            console.error('Error updating macro:', error);
            showErrorAlert('Failed to update macro. Please try again.');
          },
        });

        // Evict enabled macros cache to ensure updates are visible across all partners
        client.cache.evict({
          fieldName: 'getEnabledMacros',
        });
        client.cache.gc();

        if (partnerId === PLATFORM_PARTNER_ID) {
          refreshPlatformMacroCaches();
        }

        showSuccessAlert('Macro has been successfully updated');
      } catch (error) {
        console.error('Error during macro update process:', error);
        showErrorAlert('Failed to update macro. Please check the changes and try again.');
      }
    },
    [
      updateMacroMutation,
      settingsMacros,
      createMacroToAssigneeGroupMutation,
      deleteMacroToAssigneeGroupMutation,
      showSuccessAlert,
      showErrorAlert,
      requesterId,
      client,
      refreshPlatformMacroCaches,
    ],
  );

  const deleteMacro = useCallback(
    (macroId: string) => {
      const targetPartnerId = settingsMacros.find((macro) => macro.id === macroId)?.partnerId;
      const omitIsolationModeHeader = targetPartnerId === PLATFORM_PARTNER_ID;

      const response = deleteMacroMutation({
        variables: { params: { macroId } },
        optimisticResponse: {
          deleteMacroWithRelatedData: {
            macroId,
          },
        },
        context: omitIsolationModeHeader ? { omitIsolationModeHeader: true } : undefined,
        update(cache) {
          const id = cache.identify({
            __typename: 'Macro',
            id: macroId,
          });

          cache.evict({ id });

          // Also evict enabled macros cache to ensure deletion is visible across all partners
          cache.evict({
            fieldName: 'getEnabledMacros',
          });

          cache.gc();
        },
      });

      if (omitIsolationModeHeader) {
        refreshPlatformMacroCaches();
      }

      return response;
    },
    [deleteMacroMutation, settingsMacros, refreshPlatformMacroCaches],
  );

  const disableMacro = useCallback(
    (macroId: string) => {
      return disableMacroMutation({
        variables: { macroId, partnerId },
        optimisticResponse: {
          disableMacro: {
            macroId,
            success: true,
          },
        },
        update(cache) {
          cache.updateQuery<GetPartnerSettingValueQuery, QueryVariables>(
            {
              query: GET_PARTNER_SETTINGS_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'partnerSettingId',
                      operator: '=',
                      value: PartnerSetting.DisabledMacros,
                    },
                  ],
                },
              },
            },
            (data) => {
              if (!data) return data;

              const recordToUpdate = data.getPartnerSettingValue.records.find(
                (record) => record.partnerId === partnerId,
              );

              if (!recordToUpdate) {
                return {
                  getPartnerSettingValue: {
                    __typename: 'PartnerSettingValueDataResponse',
                    records: [
                      ...data.getPartnerSettingValue.records,
                      {
                        partnerId: partnerId ?? 0,
                        value: macroId,
                        id: 'new',
                        partnerSettingId: PartnerSetting.DisabledMacros,
                        partnerSettingValueId: 'new',
                        updatedAt: new Date().toISOString(),
                        createdAt: new Date().toISOString(),
                      },
                    ],
                  },
                };
              }

              return {
                getPartnerSettingValue: {
                  __typename: 'PartnerSettingValueDataResponse',
                  records: data.getPartnerSettingValue.records.map((record) => {
                    if (
                      record.partnerSettingValueId !== recordToUpdate.partnerSettingValueId
                    ) {
                      return record;
                    }

                    const tempDisabledMacros = (record.value || '')
                      .split(',')
                      .filter((item) => item);

                    return {
                      ...record,
                      value: [...tempDisabledMacros, macroId].join(','),
                    };
                  }),
                },
              };
            },
          );
        },
      });
    },
    [disableMacroMutation, partnerId],
  );

  const enableMacro = useCallback(
    (macroId: string) => {
      return enableMacroMutation({
        variables: { macroId, partnerId },
        optimisticResponse: {
          enableMacro: {
            macroId,
            success: true,
          },
        },
        update(cache) {
          cache.updateQuery<GetPartnerSettingValueQuery, QueryVariables>(
            {
              query: GET_PARTNER_SETTINGS_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'partnerSettingId',
                      operator: '=',
                      value: PartnerSetting.DisabledMacros,
                    },
                  ],
                },
              },
            },
            (data) => {
              if (!data) return data;

              const recordToUpdate = data.getPartnerSettingValue.records.find(
                (record) => record.partnerId === partnerId,
              );

              if (!recordToUpdate) {
                return data;
              }

              return {
                getPartnerSettingValue: {
                  __typename: 'PartnerSettingValueDataResponse',
                  records: data.getPartnerSettingValue.records.map((record) => {
                    if (
                      record.partnerSettingValueId !== recordToUpdate.partnerSettingValueId
                    ) {
                      return record;
                    }

                    const tempDisabledMacros = (record.value || '')
                      .split(',')
                      .filter((item) => item && item !== macroId);

                    return {
                      ...record,
                      value: tempDisabledMacros.join(','),
                    };
                  }),
                },
              };
            },
          );
        },
      });
    },
    [enableMacroMutation, partnerId],
  );

  return {
    macros: settingsMacros,
    enabledMacros,
    disabledMacros,
    createMacro,
    updateMacro,
    deleteMacro,
    disableMacro,
    enableMacro,
    createMacroLoading,
    updateMacroLoading,
    createMacroToAssigneeGroupLoading,
    deleteMacroToAssigneeGroupLoading,
    enableMacroLoading,
    disableMacroLoading,
    ...rest,
  };
};

type UseChatMacrosOptions = QueryHookOptions<GetEnabledMacrosQuery> & {
  conversationPartnerId?: number | null;
};

export const useChatMacros = (options?: UseChatMacrosOptions) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { user } = useCurrentUserProfile();
  const requesterId = user?.id;
  const conversationPartnerId = options?.conversationPartnerId ?? null;
  const skipOption = options?.skip;
  const onErrorOption = options?.onError;

  const partnerId = isNullish(conversationPartnerId)
    ? selectedPartnerId
    : conversationPartnerId;

  const { data: disabledMacrosIds } = usePartnerSettings({
    partnerSetting: PartnerSetting.DisabledMacros,
    partnerId,
    splitPartnerAndPlatformData: true,
  });

  const { includePlatformLevelData: includePlatformMacros } = usePlatformCatalogAccess(
    'Macro',
    partnerId,
  );

  const { showErrorAlert } = useNotifications();

  const queryOptions = useMemo<QueryHookOptions<GetEnabledMacrosQuery>>(
    () => ({
      skip: !requesterId || skipOption,
      variables: {
        partnerId,
        requesterId,
      },
      context: {
        includePlatformLevelData: includePlatformMacros,
      },
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong when fetching macros, please try again');
        onErrorOption?.(error);
      },
    }),
    [includePlatformMacros, requesterId, skipOption, partnerId, showErrorAlert, onErrorOption],
  );

  const { data: chatMacrosData, ...rest } = useQuery<GetEnabledMacrosQuery>(
    GET_ENABLED_MACROS_QUERY,
    queryOptions,
  );

  const userAssigneeGroupIds = useMemo(() => {
    const ids = new Set<string>();
    user?.conversationAssigneeGroups?.forEach((group) => {
      if (group?.id) {
        ids.add(String(group.id));
      }
      if (group?.conversationAssigneeGroupId) {
        ids.add(String(group.conversationAssigneeGroupId));
      }
    });
    return ids;
  }, [user?.conversationAssigneeGroups]);

  const chatMacros = useMemo<Macro[]>(() => {
    return (chatMacrosData?.getEnabledMacros || [])
      .filter((macro) => {
        if (!macro.conversationAssigneeGroups?.length) {
          return true;
        }

        return macro.conversationAssigneeGroups.some((group) => {
          const groupId = String(group.id ?? group.conversationAssigneeGroupId ?? '');
          return userAssigneeGroupIds.has(groupId);
        });
      })
      .filter(excludePlatformWhenDenied(includePlatformMacros))
      .filter(
        (it) =>
          !disabledMacrosIds.platformLevel.includes(it.id) &&
          !disabledMacrosIds.partnerLevel.includes(it.id),
      )
      .map(mapMacroResponseToUI)
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
  }, [
    chatMacrosData?.getEnabledMacros,
    disabledMacrosIds,
    userAssigneeGroupIds,
    includePlatformMacros,
  ]);

  return {
    chatMacros,
    ...rest,
  };
};

function doesMacroHavePlaceholder(macro: string) {
  return /\{\{.*?\}\}/.test(macro);
}
export const useMacrosMethods = () => {
  const { showErrorAlert } = useNotifications();
  const [interpolateMacro] = useLazyQuery<GetInterpolateMacrosTemplateQuery>(
    INTERPOLATE_MACROS_TEMPLATE_QUERY,
    {
      context: {
        ttl: 0,
      },
      fetchPolicy: 'no-cache',
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          'Something went wrong when interpolating macro, using original template',
        );
      },
    },
  );

  const interpolateMacroTemplate = useCallback(
    async (input: { text: string; conversationId: string }) => {
      if (!doesMacroHavePlaceholder(input.text)) {
        return Promise.resolve(input.text);
      }

      try {
        const result = await interpolateMacro({
          variables: { input },
        });
        return result?.data?.getTextInterpolation || input.text;
      } catch {
        return input.text;
      }
    },
    [interpolateMacro],
  );

  return {
    interpolateMacroTemplate,
  };
};

function addNewMacroToCache(
  client: ApolloClient<unknown>,
  queryOptions?: QueryHookOptions<GetSettingsMacrosQuery>,
) {
  return function (newMacro?: FetchedMacroRaw) {
    if (!newMacro) return;

    // Update GET_SETTINGS_MACROS_QUERY cache
    client.cache.updateQuery<GetSettingsMacrosQuery>(
      {
        query: GET_SETTINGS_MACROS_QUERY,
        ...queryOptions,
      },
      (cache) => {
        if (!cache) return null;

        const { getManagementMacros: records } = cache;
        let updatedMacrosList = records;

        const existingMacroRecord = records.find((record) => record.id === newMacro.id);
        if (existingMacroRecord) {
          updatedMacrosList = records.map((macro) =>
            macro.id === newMacro.id ? newMacro : macro,
          );
        } else {
          updatedMacrosList = [...records, newMacro];
        }

        return {
          ...cache,
          getManagementMacros: updatedMacrosList,
        };
      },
    );

    // Also update GET_ENABLED_MACROS_QUERY cache to ensure macro appears in chat view
    // Note: We evict the entire field in createMacro to handle multi-partner scenarios,
    // so this optimistic update is primarily for immediate feedback
    client.cache.updateQuery<GetEnabledMacrosQuery>(
      {
        query: GET_ENABLED_MACROS_QUERY,
        variables: queryOptions?.variables,
      },
      (cache) => {
        if (!cache) return null;

        const { getEnabledMacros: records } = cache;
        const existingMacroRecord = records.find((record) => record.id === newMacro.id);

        let updatedMacrosList;
        if (existingMacroRecord) {
          updatedMacrosList = records.map((macro) =>
            macro.id === newMacro.id ? newMacro : macro,
          );
        } else {
          updatedMacrosList = [...records, newMacro];
        }

        return {
          ...cache,
          getEnabledMacros: updatedMacrosList,
        };
      },
    );
  };
}

export function getCachedMacro(cache: ApolloCache<unknown>, macroId?: string) {
  if (!macroId) {
    return;
  }

  const fragment = gql`
    fragment CurrentMacro on Macro {
      ...MacroFields
      ...MacroRelations
    }
    ${MACRO_FIELDS}
    ${MACRO_RELATIONS}
  `;

  const id = cache.identify({
    __typename: 'Macro',
    id: macroId,
  });

  return cache.readFragment<MacroRaw>({
    id,
    fragment,
    fragmentName: 'CurrentMacro',
  });
}
